/**
 * The audit log's cache.
 *
 * **Nothing polls.** A log is read when somebody opens it; a background refresh per open tab
 * would multiply traffic against a table that only grows, for rows nobody is watching for.
 * The ecosystem rule's default answer — no — with no reason to deviate here.
 *
 * **The filter is part of the key.** Two filters are two questions with two answers, and the
 * object is total (`./filters.ts`), so it hashes to one stable key rather than to whichever
 * subset of thirteen optional properties happened to be set.
 */

import { infiniteQueryOptions, useInfiniteQuery, useMutation } from '@tanstack/react-query';

import { PROJECTS_QUERY_KEY } from '@/features/projects';

import { exportActivityCsv, readActivity } from './api';
import { NO_FILTERS } from './filters';
import type { ActivityFilters } from './filters';

export function activityQueryOptions(
  projectId: string,
  // C1's Overview card asks the unfiltered question and says so by omission; C9 always passes
  // what the URL is holding.
  filters: ActivityFilters = NO_FILTERS,
) {
  return infiniteQueryOptions({
    queryKey: [...PROJECTS_QUERY_KEY, 'activity', projectId, filters] as const,
    queryFn: ({ pageParam }) => readActivity(projectId, filters, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor ?? null,
    staleTime: 30_000,
  });
}

export function useActivity(projectId: string, filters: ActivityFilters) {
  return useInfiniteQuery(activityQueryOptions(projectId, filters));
}

/**
 * The filter on screen, as a file.
 *
 * A mutation rather than a query, because it is an act with a side effect on the browser and
 * not a thing to cache: the same filter exported twice is two files, and a cached CSV would be
 * a stale one the moment anything else is recorded.
 *
 * The blob is turned into a download here rather than followed as a URL, because this endpoint
 * answers the bytes directly and the request has to carry the session — the drawings download
 * goes the other way (a signed URL the browser follows) because there the API never sees a
 * byte. The object URL is revoked immediately: the anchor click is synchronous, so by the time
 * this line runs the browser already holds the blob.
 */
export function useExportActivity(projectId: string) {
  return useMutation({
    mutationFn: async (filters: ActivityFilters) => {
      const blob = await exportActivityCsv(projectId, filters);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'activity.csv';
      anchor.click();
      URL.revokeObjectURL(url);
    },
  });
}
